import Modal from "@via/components/modal.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import { ModalSize } from "@via/utils/constants.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import SelectComponent from "@via/components/select/select.tsx";
import {
  useCreateOrganizationAssumeRoleCredential,
  useDeleteOrganizationAssumeRoleCredential,
  useGetOrganizationAllowedRoleArns,
  useGetOrganizationAssumeRoleCredentials,
  useUpdateOrganizationAssumeRoleCredential,
} from "@via/pages/organization/organization.api.tsx";
import { type JSX, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  maxLengthValidation,
  requiredValidation,
} from "@via/utils/form-validators.tsx";

type FormState = {
  name: string;
  roleArn: string;
  externalId: string;
};

const DEFAULT_FORM: FormState = { name: "", roleArn: "", externalId: "" };

export default function AssumeRoleCredentialModal(props: {
  organizationId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}): JSX.Element {
  const { organizationId, open, setOpen } = props;

  const { data: credentials = [], isLoading } =
    useGetOrganizationAssumeRoleCredentials(organizationId, {
      enabled: open && !!organizationId,
    });

  const { data: allowedRoleArns = [], isLoading: isAllowedLoading } =
    useGetOrganizationAllowedRoleArns(organizationId, {
      enabled: open && !!organizationId,
    });

  const { mutateAsync: createCredential, isPending: isCreating } =
    useCreateOrganizationAssumeRoleCredential();
  const { mutateAsync: updateCredential, isPending: isUpdating } =
    useUpdateOrganizationAssumeRoleCredential();
  const { mutateAsync: deleteCredential, isPending: isDeleting } =
    useDeleteOrganizationAssumeRoleCredential();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [credentialToDelete, setCredentialToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm<FormState>({ mode: "onChange", defaultValues: DEFAULT_FORM });

  const form = watch();

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      reset(DEFAULT_FORM);
    }
  }, [open, reset]);

  const selectedArn = (form.roleArn ?? "").trim();
  const isSelectedArnAllowed =
    selectedArn.length > 0 && allowedRoleArns.includes(selectedArn);

  const isSaving = isCreating || isUpdating;
  const canSubmit =
    !!organizationId && isValid && !isSaving && isSelectedArnAllowed;

  const roleArnOptions = allowedRoleArns.map(a => ({ label: a, value: a }));

  const roleArnPlaceholder = useMemo(() => {
    if (isAllowedLoading) return "Loading approved role ARNs…";
    if (allowedRoleArns.length === 0) return "No approved role ARNs configured";
    return "Select an approved role ARN";
  }, [allowedRoleArns.length, isAllowedLoading]);

  const roleArnSelectValue = useMemo(() => {
    if (!selectedArn) return null;
    return { label: selectedArn, value: selectedArn };
  }, [selectedArn]);

  const onSubmit = handleSubmit(async payload => {
    if (!organizationId) return;
    const requestBody = {
      name: payload.name.trim(),
      roleArn: payload.roleArn.trim(),
      externalId: payload.externalId.trim() || undefined,
    };
    if (editingId) {
      await updateCredential({
        organizationId,
        credentialId: editingId,
        requestBody,
      });
    } else {
      await createCredential({ organizationId, requestBody });
    }
    setEditingId(null);
    reset(DEFAULT_FORM);
  });

  const startEdit = (cred: (typeof credentials)[number]) => {
    setEditingId(cred.id);
    reset({
      name: cred.name,
      roleArn: cred.roleArn,
      externalId: cred.externalId ?? "",
    });
    // reset() leaves the form unvalidated, so isValid stays false and the Update
    // button would sit disabled until the user edits something. Validate now.
    void trigger();
  };

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        size={ModalSize.Large}
        width={900}
        title="Configure Assume-Role Credentials"
        showButtons={false}>
        <div className="space-y-6">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            Create AWS IAM assume-role credentials for runs. Each credential
            stores only an approved role ARN and an optional external ID, never
            a long-lived key.
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                Organization assume-role credentials
              </h3>
            </div>
            {isLoading ? (
              <div className="p-4">
                <Spinner loading />
              </div>
            ) : credentials.length === 0 ? (
              <div className="p-4 text-sm text-gray-600">
                No assume-role credentials configured yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {credentials.map(c => (
                  <div
                    key={c.id}
                    className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {c.name}
                      </p>
                      <p className="text-sm text-gray-600 break-all">
                        {c.roleArn}
                      </p>
                      {c.externalId ? (
                        <p className="text-xs text-gray-500">
                          External ID: {c.externalId}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(c)}
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setCredentialToDelete({ id: c.id, name: c.name })
                        }
                        className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50"
                        disabled={isDeleting}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900">
              {editingId
                ? "Update assume-role credential"
                : "Add assume-role credential"}
            </h3>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <FormInputComponent
                  control={control}
                  name="name"
                  label="Name"
                  placeholder="e.g. Prod runs"
                  validations={{
                    ...requiredValidation("Name"),
                    ...maxLengthValidation(
                      45,
                      "Name must be 45 characters or fewer"
                    ),
                  }}
                />
              </div>

              <div>
                <FormInputComponent
                  control={control}
                  name="externalId"
                  label="External ID (optional)"
                  placeholder="Cross-account external id"
                  validations={{
                    ...maxLengthValidation(
                      255,
                      "External ID must be 255 characters or fewer"
                    ),
                  }}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-900">
                  Role ARN
                </label>
                <div className="mt-2">
                  <SelectComponent
                    control={control}
                    name="roleArn"
                    options={roleArnOptions}
                    enableClear={false}
                    isSearchable
                    placeholder={roleArnPlaceholder}
                    isLoading={isAllowedLoading}
                    readOnly={isAllowedLoading || allowedRoleArns.length === 0}
                    value={roleArnSelectValue}
                    rules={{
                      required: "Role ARN is required",
                      validate: (value: string) => {
                        const t = (value ?? "").trim();
                        if (!t) return "Role ARN is required";
                        return (
                          allowedRoleArns.some(a => a === t) ||
                          "Role ARN is not approved by system admin."
                        );
                      },
                    }}
                  />
                </div>

                {errors.roleArn?.message ? (
                  <p className="mt-1 text-xs text-rose-500">
                    {String(errors.roleArn.message)}
                  </p>
                ) : null}

                {allowedRoleArns.length === 0 && !isAllowedLoading ? (
                  <p className="mt-1 text-xs text-gray-500">
                    Ask a system admin to approve a role ARN before adding a
                    credential.
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              {editingId ? (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    reset(DEFAULT_FORM);
                  }}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                  Cancel edit
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void onSubmit()}
                disabled={!canSubmit}
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
                <Spinner loading={isSaving}>
                  {editingId ? "Update" : "Add"}
                </Spinner>
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {credentialToDelete && (
        <DeleteModal
          item={credentialToDelete}
          setItem={setCredentialToDelete}
          title="Delete assume-role credential"
          confirmText="Delete"
          message={() => (
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                This will permanently delete{" "}
                <strong>{credentialToDelete.name}</strong>.
              </p>
              <p>
                Deletion is refused while a run environment still uses this
                credential. Point those run environments at another credential
                first.
              </p>
            </div>
          )}
          isLoading={isDeleting}
          deleteFunction={async () => {
            await deleteCredential({
              organizationId,
              credentialId: credentialToDelete.id,
            });
          }}
        />
      )}
    </>
  );
}
